import React, { useState, useEffect } from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Target, Users, Award, Lightbulb, TrendingUp, Shield } from 'lucide-react';
import { team as mockTeam } from '../mockData';
import { getTeam } from '../services/api';

export const About = () => {
  const [team, setTeam] = useState(mockTeam);

  useEffect(() => {
    const fetchTeam = async () => {
      try {
        const data = await getTeam();
        if (data.length > 0) setTeam(data);
      } catch (error) {
        console.log('Using mock team');
      }
    };
    fetchTeam();
  }, []);
  return (
    <div className="min-h-screen pt-24 pb-16">
      {/* Hero Section */}
      <section className="py-16 px-4 bg-gradient-to-br from-orange-50 via-blue-50 to-white">
        <div className="container mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h1 className="text-5xl lg:text-6xl font-bold mb-6">
                About <span className="text-orange-500">SolarBull</span>
              </h1>
              <p className="text-xl text-gray-600 mb-6 leading-relaxed">
                SolarBull Energy LLP is a leading Commercial & Industrial (C&I) solar EPC and consultancy 
                firm operating across India since 2018. We specialize in rooftop and ground-mounted solar 
                power projects, including Open Access and PM-KUSUM initiatives.
              </p>
              <p className="text-lg text-gray-600 leading-relaxed">
                With over 65 MW of cumulative installed capacity and 9+ years of experience, we have 
                established ourselves as a trusted partner for residential, commercial, and industrial 
                solar projects. Our expertise spans complete EPC solutions, O&M services, and project 
                management consultancy across Telangana and India.
              </p>
            </div>
            <div className="relative">
              <img
                src="/images/extra.jpg"
                alt="Solar Installation"
                className="rounded-2xl shadow-2xl" />

            </div>
          </div>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="py-20 px-4 bg-white">
        <div className="container mx-auto">
          <div className="grid md:grid-cols-2 gap-8">
            <Card className="border-none shadow-xl hover:shadow-2xl transition-shadow duration-300">
              <CardContent className="p-10">
                <div className="w-16 h-16 bg-gradient-to-br from-orange-500 to-orange-600 rounded-xl flex items-center justify-center mb-6">
                  <Target className="h-8 w-8 text-white" />
                </div>
                <h3 className="text-3xl font-bold mb-4 text-gray-900">Our Mission</h3>
                <p className="text-gray-600 text-lg leading-relaxed">
                  To accelerate India's transition to clean energy by providing world-class 
                  solar solutions that are accessible, reliable, and economically viable for 
                  every home and business.
                </p>
              </CardContent>
            </Card>

            <Card className="border-none shadow-xl hover:shadow-2xl transition-shadow duration-300">
              <CardContent className="p-10">
                <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl flex items-center justify-center mb-6">
                  <Lightbulb className="h-8 w-8 text-white" />
                </div>
                <h3 className="text-3xl font-bold mb-4 text-gray-900">Our Vision</h3>
                <p className="text-gray-600 text-lg leading-relaxed">
                  To be the most trusted and innovative solar EPC company in India, 
                  empowering communities with sustainable energy solutions and contributing 
                  to a greener planet for future generations.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-20 px-4 bg-gradient-to-br from-gray-50 to-white">
        <div className="container mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl lg:text-5xl font-bold mb-4">
              Our <span className="text-orange-500">Values</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              The principles that guide everything we do
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
            {
              icon: Award,
              title: 'Quality Excellence',
              desc: 'We use only tier-1 solar panels, each and every component, ensuring maximum efficiency and longevity for every installation.'
            },
            {
              icon: Users,
              title: 'Customer First',
              desc: 'Your satisfaction is our priority. We provide standardised solutions and dedicated support throughout your solar journey.'
            },
            {
              icon: Shield,
              title: 'Trust & Transparency',
              desc: 'Clear communication, honest pricing, and reliable service. We build lasting relationships based on trust.'
            },
            {
              icon: TrendingUp,
              title: 'Innovation',
              desc: 'Staying ahead with the latest solar technology and installation techniques to deliver superior performance.'
            },
            {
              icon: Target,
              title: 'Sustainability',
              desc: 'Committed to environmental responsibility and helping reduce carbon footprint through clean energy adoption.'
            },
            {
              icon: Lightbulb,
              title: 'Expertise',
              desc: 'Our certified team brings decades of combined experience in solar EPC, O&M, ensuring professional execution and maintenance.'
            }].
            map((value, index) =>
            <Card key={index} className="border-none shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105 group">
                <CardContent className="p-8 text-center">
                  <div className="w-16 h-16 bg-orange-100 rounded-xl flex items-center justify-center mx-auto mb-6 group-hover:bg-orange-500 transition-colors duration-300">
                    <value.icon className="h-8 w-8 text-orange-600 group-hover:text-white transition-colors duration-300" />
                  </div>
                  <h3 className="text-xl font-bold mb-3 text-gray-900">{value.title}</h3>
                  <p className="text-gray-600 leading-relaxed">{value.desc}</p>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </section>

      {/* Team Section */}
      <section className="py-20 px-4 bg-white">
        <div className="container mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl lg:text-5xl font-bold mb-4">
              Meet Our <span className="text-orange-500">Team</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Experienced professionals dedicated to your solar success
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {team.map((member) =>
            <Card key={member.id} className="border-none shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105">
                <CardContent className="p-8 text-center">
                  <img
                  src={member.image}
                  alt={member.name}
                  className="w-40 h-40 rounded-full mx-auto mb-6 border-4 border-orange-100 object-cover object-top" />

                  <h3 className="text-2xl font-bold mb-2 text-gray-900">{member.name}</h3>
                  <p className="text-orange-600 font-semibold mb-4">{member.designation}</p>
                  <p className="text-gray-600 leading-relaxed text-sm mb-4">{member.bio}</p>
                  {member.description &&
                <p className="text-gray-500 leading-relaxed text-xs">{member.description}</p>
                }
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-20 px-4 bg-gradient-to-r from-orange-500 to-orange-600">
        <div className="container mx-auto">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center text-white">
            <div>
              <div className="!font-bold !text-5xl mb-2">9+</div>
              <p className="text-lg text-white/90">Years Experience</p>
            </div>
            <div>
              <div className="text-5xl font-bold mb-2">150+</div>
              <p className="text-lg text-white/90">Projects Completed</p>
            </div>
            <div>
              <div className="text-5xl font-bold mb-2">65+ MW</div>
              <p className="text-lg text-white/90">Installed Capacity</p>
            </div>
            <div>
              <div className="text-5xl font-bold mb-2">100+</div>
              <p className="text-lg text-white/90">Happy Customers</p>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-20 px-4 bg-white">
        <div className="container mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl lg:text-5xl font-bold mb-4">
              Why Choose <span className="text-orange-500">SolarBull</span>
            </h2>
          </div>

          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="space-y-6">
              {[
              'Certified and experienced solar engineers',
              'Premium tier-1 solar panels with 30-year warranty',
              'Transparent pricing with no hidden costs',
              'Fast installation with minimal disruption',
              'Comprehensive after-sales support and maintenance',
              'Government subsidy assistance',
              'Performance monitoring and optimization',
              'Proven track record of 300+ successful installations'].
              map((item, index) =>
              <div key={index} className="flex items-start space-x-4">
                  <div className="w-6 h-6 bg-orange-500 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                    <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <p className="text-lg text-gray-700">{item}</p>
                </div>
              )}
            </div>
            <div>
              <img
                src="/images/extra.jpg"
                alt="Solar Panels"
                className="rounded-2xl shadow-2xl" />

            </div>
          </div>
        </div>
      </section>
    </div>);

};